import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import AILanguageSettings from '@/features/CreateWorkspace/AILanguageSettings';
import DataAnonymizeSetting from '@/features/CreateWorkspace/DataAnonymizeSetting';
import WorkspaceNameSettings from '@/features/CreateWorkspace/WorkspaceNameSettings';
import useColor from '@/hooks/domain/useColor';
import useSidebar from '@/hooks/domain/useSidebar';
import useWorkspaceLanguageSelect from '@/hooks/domain/useWorkspaceLanguageSelect';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import CreateWorkspaceError from '@/services/workspace/CreateWorkspaceErrorHandler';
import useCommonStore from '@/stores/pure/CommonStore';
import usePopupStore from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { track } from '@/utils/track/track';

import { GORGIAS_ACCOUNT_SEARCH_PARAM } from '../Setting/Integrations/integrationRedirection/gorgias-redirect';

const ONBOARDING_STEPS = [
  {
    title: '👋 Welcome to Syncly',
    direction: {
      title: 'Name your workspace',
      desc: 'Of course, You can edit the name later',
    },
  },
  {
    title: '✨ What language do you prefer?',
    direction: {
      title: 'Select language setting for Syncly AI',
      desc: 'AI will deliver the output in the language of your choice.',
    },
  },
  {
    title: '🔒 If you want to protect data',
    direction: {
      title: 'Data anonymization',
      desc: 'Sensitive information like PII will NOT be ingested into Syncly workspace',
    },
  },
];

const CreateWorkspace = () => {
  const color = useColor();
  const addToast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [workspaceName, setWorkspaceName] = useState('');
  const [dataAnonymization, setDataAnonymization] = useState(false);
  const { user } = useUserStore((state) => ({ user: state.user }));
  const createWorkspace = useWorkspaceStore((state) => state.createWorkspace);
  const initStoreWhenChangeWorkspace = useCommonStore(
    (state) => state.initStoreWhenChangeWorkspace,
  );
  const wc = useWorkspaceRouter();
  const gorgias_account = searchParams.get(GORGIAS_ACCOUNT_SEARCH_PARAM);

  useSidebar(false);

  useEffect(() => {
    if (!user) {
      wc.navigate('/login', {
        replace: true,
      });
    }
  }, [user, wc]);

  const setPopup = usePopupStore((state) => state.setPopup);

  const [step, setStep] = useState(0);
  const [langOptions, language, setLanguage] = useWorkspaceLanguageSelect();

  const onCompleteWorkspaceName = async () => {
    if (!workspaceName) {
      setPopup({
        popupTheme: 'neutral',
        type: 'alert',
        title: 'Oops!',
        message: 'To continue, please make sure to add a workspace name.',
        confirmText: 'Got it',
      });
      return;
    }
    const exists =
      await service.workspace.checkWorkspaceNameDuplication(workspaceName);
    if (exists) {
      addToast('Workspace name already exists.', 'fail');
    } else {
      track(AMP_KEYS.ONBOARDING_STEP_COMPLETE, {
        step: 1,
      });
      moveStep('next');
    }
  };

  const moveStep = (dir: 'prev' | 'next') => {
    if (dir === 'prev') {
      setStep((prev) => prev - 1);
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const onCompleteAILanguageSettings = async () => {
    if (!language) return addToast('Language must be selected', 'fail');
    track(AMP_KEYS.ONBOARDING_STEP_COMPLETE, {
      step: 2,
    });
    moveStep('next');
  };

  const onCompleteDataAnonymize = async () => {
    if (!language) return addToast('Language must be selected', 'fail');
    initStoreWhenChangeWorkspace();
    try {
      const res = await createWorkspace({
        name: workspaceName,
        language: language,
        data_anonymization: dataAnonymization,
      });
      track(AMP_KEYS.ONBOARDING_STEP_COMPLETE, {
        step: 3,
      });
      if (gorgias_account) {
        navigate(
          `/workspace/${res.alias}/settings/integration?${GORGIAS_ACCOUNT_SEARCH_PARAM}=${gorgias_account}`,
        );
      } else {
        wc.navigate(`/create-workspace-complete?workspace_alias=${res.alias}`);
      }
    } catch (e) {
      if (e instanceof CreateWorkspaceError) {
        setPopup({
          popupTheme: 'neutral',
          type: 'alert',
          title: 'Oops!',
          message: e.message,
          confirmText: 'Try again',
        });
      }
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <WorkspaceNameSettings
            workspaceName={workspaceName}
            setWorkspaceName={setWorkspaceName}
            onComplete={onCompleteWorkspaceName}
          />
        );
      case 1:
        return (
          <AILanguageSettings
            langOptions={langOptions}
            language={language}
            setLanguage={(lang) => {
              track(AMP_KEYS.ONBOARDING_LANGUAGE_SET, {
                lang,
              });
              setLanguage(lang);
            }}
            undo={() => {
              track(AMP_KEYS.ONBOARDING_STEP_UNDO, {
                to: 1,
              });
              moveStep('prev');
            }}
            onComplete={onCompleteAILanguageSettings}
          />
        );
      case 2:
        return (
          <DataAnonymizeSetting
            dataAnonymization={dataAnonymization}
            setDataAnonymization={(v) => {
              track(AMP_KEYS.ONBOARDING_DATA_ANONYMIZATION_TOGGLE, {
                status: v,
              });
              setDataAnonymization(v);
            }}
            undo={() => {
              track(AMP_KEYS.ONBOARDING_STEP_UNDO, {
                to: 2,
              });
              moveStep('prev');
            }}
            onComplete={onCompleteDataAnonymize}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Style j="center" a="center">
      <VFlex
        width="648px"
        style={{
          position: 'relative',
        }}
      >
        <Typo
          weight="b"
          color={color.gray700}
          style={{
            fontSize: '32px',
            lineHeight: '32px',
          }}
        >
          {ONBOARDING_STEPS[step].title}
        </Typo>

        <Space h={26} />

        <Flex
          g={12}
          padding="32px"
          style={{
            backgroundColor: color.gray100,
            borderRadius: '8px',
          }}
        >
          <Flex
            j="center"
            a="center"
            width="24px"
            height="24px"
            style={{
              backgroundColor: color.primary100,
              borderRadius: '50%',
              color: color.NL100,
            }}
          >
            {step + 1}
          </Flex>

          <VFlex g={8}>
            <Typo type="label0" weight="b" color={color.gray700}>
              {ONBOARDING_STEPS[step].direction.title}
            </Typo>

            <Typo type="label2" color={color.gray700}>
              {ONBOARDING_STEPS[step].direction.desc}
            </Typo>
          </VFlex>
        </Flex>

        <Space h={12} />

        <VFlex
          d="column"
          j="space-between"
          padding="32px"
          style={{
            backgroundColor: color.gray100,
            borderRadius: '8px',
          }}
        >
          {renderStep()}
        </VFlex>
      </VFlex>
    </Style>
  );
};

const Style = styled(Flex)`
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.gray200};

  .direction .create-workspace-wrapper {
    width: 100%;
    max-width: 400px;
  }
  .workspace-info-wrapper {
    width: 400px;
  }
`;

export default CreateWorkspace;
